// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Dialog.tsx
 * @input Uses React, DialogHTMLAttributes, ReactNode, container (Layout), DialogContext
 * @output Exports Dialog component, DialogProps, DialogVariant, DialogPurpose types
 * @position Core implementation; consumed by index.ts, tested by Dialog.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Dialog/Dialog.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Dialog/Dialog.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Dialog/index.ts (exports if types change)
 * - /apps/storybook/stories/Dialog.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/Dialog/ (showcase blocks)
 */
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useScrollLock } from "../hooks/useScrollLock.js";
import { hasActiveFocusTrapEscape, isImeKeyEvent } from "../hooks/useFocusTrap.js";
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, durationVars, easeVars, shadowVars } from "../theme/tokens.stylex.js";
import { container } from "../Layout/container.stylex.js";
import { paddingStyles, containerPaddingInlineVarStyles, containerPaddingBlockStartVarStyles, containerPaddingBlockEndVarStyles, spacingStepToToken } from "../Layout/padding.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { devWarn } from "../utils/devWarning.js";
import { DialogContext } from "./DialogContext.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Calculate a directional translate offset for dialog entry animation.
 * Returns a normalized vector from the trigger element toward the viewport
 * center, scaled to the given distance.
 */
function getDialogDirection(triggerEl, distance = 16) {
  const rect = triggerEl.getBoundingClientRect();
  const dx = rect.left + rect.width / 2 - window.innerWidth / 2;
  const dy = rect.top + rect.height / 2 - window.innerHeight / 2;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  return {
    x: Math.round(dx / dist * distance),
    y: Math.round(dy / dist * distance)
  };
}

/**
 * Dialog variant type
 * - standard: Normal dialog with configurable width/height
 * - fullscreen: Takes up the entire viewport
 *
 * Extensible via module augmentation of DialogVariantMap.
 */

/**
 * Dialog purpose type - controls dismissal behavior
 * - required: Mandatory flows (security, permissions) - disables all exit methods
 * - form: User forms/flows - prevents backdrop click, allows escape
 * - info: Informational flows - allows all exit methods
 */

/** Block-axis offsets — always allowed, independent of inline direction. */

/**
 * Static position for a dialog. The inline axis is logical XOR physical — the
 * type forbids mixing the two, so a single dialog can't be positioned both ways:
 * - Logical `start`/`end` map to `inset-inline-*` and mirror under RTL (preferred).
 * Block-axis `top`/`bottom` may be combined with either.
 */

const enterDirectional = "x11lauxq-B";

/**
 * Dialog styles using native <dialog> element
 * Uses ::backdrop pseudo-element for overlay
 */
const styles = {
  dialog: {
    kVAEAm: "xixxii4",
    kogj98: "x1bpp3o7",
    kmVPX3: "x1717udv",
    kWkggS: "x10xzikg",
    "--_dialog-radius": "xvuvksw",
    kaIpWk: "xuacgfc",
    kGVxlE: "x1kcpxr7",
    k1xSpc: "x1s85apg",
    kXwgrk: "xdt5ytf",
    kZKoxP: "xg7h5cd",
    kZeWKH: "xish69e",
    kSiTet: "xg01cxk",
    k44tkh: "xqgcaz",
    kyAemX: "x128ha8g",
    kWV6AL: "xskzprw",
    kI3sdo: "x17nn4n9",
    kInvED: "x1wfwxd8 x7s97pk",
    $$css: true
  },
  open: {
    k1xSpc: "x78zum5",
    kSiTet: "x1hc1fzr",
    kKVMdj: "x1ewfqum x1aquc0h",
    $$css: true
  },
  backdrop: {
    kGyWv1: "xnixb3f",
    kba3nw: "x1abwkk1",
    $$css: true
  },
  fullscreen: {
    kzqmXN: "x1o6l61p",
    kZKoxP: "xtdtrs8",
    ks0D6T: "xlbgzzq",
    kskxy: "x1wj9ous",
    kaIpWk: "x2u8bby",
    kogj98: "x1ghz6dp",
    kpwlN0: "x10a8y8t",
    $$css: true
  },
  inner: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kUk6DE: "x12lumcd",
    kAzted: "x2lwn1j",
    kVQacm: "xb3r6kr",
    kaIpWk: "x1pjcqnp",
    $$css: true
  },
  inlineWrapper: {
    kmVPX3: "x1717udv",
    kWkggS: "x10xzikg",
    "--_dialog-radius": "xvuvksw",
    kaIpWk: "xuacgfc",
    kGVxlE: "x1kcpxr7",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kZKoxP: "xg7h5cd",
    kZeWKH: "xish69e",
    $$css: true
  }
};

// Dynamic styles for width, maxHeight, and position
const _temp = {
  ks0D6T: "x95b5qq",
  "$$css": true
};
const _temp2 = {
  kogj98: "x1ghz6dp",
  "$$css": true
};
const dynamicStyles = {
  sizing: (width, maxHeight) => [_temp, {
    kzqmXN: (typeof width === 'number' ? `${width}px` : width) != null ? "x5lhr3w" : typeof width === 'number' ? `${width}px` : width,
    kskxy: (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) != null ? "x1jols5v" : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof width === 'number' ? `${width}px` : width),
    "--x-maxHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight)
  }],
  position: (top, insetInlineStart, insetInlineEnd, bottom) => [_temp2, {
    k87sOh: top != null ? "xjbys53" : top,
    kLqNvP: insetInlineStart != null ? "x1lxsm33" : insetInlineStart,
    kt4wiu: insetInlineEnd != null ? "xqxgn94" : insetInlineEnd,
    krVfgx: bottom != null ? "x1nqzi6q" : bottom,
    $$css: true
  }, {
    "--x-top": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(top),
    "--x-insetInlineStart": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(insetInlineStart),
    "--x-insetInlineEnd": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(insetInlineEnd),
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(bottom)
  }]
};

/**
 * Format position value - numbers become pixels, strings pass through, undefined becomes null
 */
function formatPosition(value) {
  return typeof value === 'number' ? `${value}px` : value;
}

/**
 * Map a {@link DialogPosition} to resolved CSS offsets. Logical `start`/`end`
 * become `inset-inline-*` (mirror under RTL); each unset offset falls back to
 * `auto`.
 *
 * Not re-exported from the package; internal to Dialog. Directly unit-tested
 * so the mapping is verified without StyleX class compilation.
 *
 * @see DialogPosition
 */
export function resolveDialogPositionOffsets(position) {
  const {
    top,
    bottom,
    start,
    end
  } = position;
  return {
    top: top !== undefined ? formatPosition(top) : 'auto',
    bottom: bottom !== undefined ? formatPosition(bottom) : 'auto',
    // Logical offsets mirror under RTL (preferred replacements).
    insetInlineStart: start !== undefined ? formatPosition(start) : 'auto',
    insetInlineEnd: end !== undefined ? formatPosition(end) : 'auto'
  };
}
/**
 * A dialog component using the native <dialog> element.
 *
 * Designed to be used with Layout as its child for structured content.
 * Uses the browser's built-in modal behavior for optimal accessibility.
 * When a DialogHeader is rendered inside, its title automatically names the
 * dialog via aria-labelledby; pass `aria-label` or `aria-labelledby` to
 * override.
 *
 * @example
 * ```
 * const [isOpen, setIsOpen] = useState(false);
 * <Dialog isOpen={isOpen} onOpenChange={open => setIsOpen(open)}>
 *   <Layout
 *     header={<DialogHeader title="Title" onOpenChange={open => setIsOpen(open)} />}
 *     content={<LayoutContent>Content</LayoutContent>}
 *     footer={<LayoutFooter hasDivider>Actions</LayoutFooter>}
 *   />
 * </Dialog>
 * ```
 */
export function Dialog({
  isOpen,
  isInline = false,
  onOpenChange,
  width = 400,
  maxHeight = '75vh',
  position,
  variant = 'standard',
  purpose = 'info',
  padding,
  children,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  // When no explicit padding prop, use theme default (--astryx-dialog-padding)
  const useThemeDefault = padding == null;
  const effectivePadding = padding ?? 4;
  const paddingToken = spacingStepToToken[effectivePadding];
  const isFullscreen = variant === 'fullscreen';

  // Default accessible name: publish a title id through DialogContext so a
  // DialogHeader applies it to its heading (mirrors AlertDialog's explicit
  // useId wiring). Whether to emit the default aria-labelledby is decided
  // imperatively in the callback ref below by checking whether the title
  // element actually rendered — so a dialog with no header never points at a
  // missing id, and we avoid the extra render a registration-via-state effect
  // would cost at the dialog level.
  const titleId = useId();
  const dialogContextValue = useMemo(() => ({
    isInline,
    titleId
  }), [isInline, titleId]);

  // Consumer-provided labels always win over the DialogHeader default.
  const hasConsumerName = props['aria-label'] != null || props['aria-labelledby'] != null;
  const dialogRef = useRef(null);

  // Callback ref on the <dialog>: sync the default aria-labelledby from the
  // DialogHeader title (if one rendered) in the same commit, with no second
  // render. Consumer aria-label/aria-labelledby win — when present we leave
  // the attribute to the {...safeProps} spread and never touch it here.
  const attachDialog = useCallback(node => {
    dialogRef.current = node;
    if (!node || hasConsumerName) {
      return;
    }
    const hasTitle = node.querySelector(`#${CSS.escape(titleId)}`) != null;
    if (hasTitle) {
      node.setAttribute('aria-labelledby', titleId);
    } else {
      node.removeAttribute('aria-labelledby');
    }
  }, [titleId, hasConsumerName]);

  // Capture the element that was focused when the dialog opened,
  // for directional animation origin and focus restoration on close.
  const triggerElementRef = useRef(null);

  // Derive dismissal behavior from purpose
  const allowEscape = purpose !== 'required';
  const allowBackdropClick = purpose === 'info';

  // Handle open/close state — skip for inline rendering
  useEffect(() => {
    if (isInline) {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    if (isOpen) {
      // Capture the currently focused element as the trigger — used for
      // directional animation origin and focus restoration on close.
      triggerElementRef.current = document.activeElement;

      // Set directional CSS custom properties before opening
      const trigger = triggerElementRef.current;
      if (trigger && trigger !== document.body) {
        const dir = getDialogDirection(trigger);
        dialog.style.setProperty('--dialog-dir-x', `${dir.x}px`);
        dialog.style.setProperty('--dialog-dir-y', `${dir.y}px`);
      } else {
        dialog.style.setProperty('--dialog-dir-x', '0px');
        dialog.style.setProperty('--dialog-dir-y', '16px');
      }
      if (!dialog.open) {
        dialog.showModal();
        // React's autoFocus calls .focus() during commit, before showModal()
        // makes the dialog visible, so the focus silently fails.
        // Focus the first element with data-autofocus inside the dialog.
        const autofocusTarget = dialog.querySelector('[data-autofocus]');
        if (autofocusTarget) {
          autofocusTarget.focus();
        }
      }
    } else {
      if (dialog.open) {
        dialog.close();
      }
      // Return focus to the element that opened the dialog
      triggerElementRef.current?.focus();
      triggerElementRef.current = null;
    }
  }, [isOpen, isInline]);

  // Lock body scroll when dialog is open (iOS Safari workaround)
  // Skip for inline rendering — no modal overlay to compensate for.
  useScrollLock(isOpen && !isInline);

  // Handle Escape key — skip for inline rendering
  useEffect(() => {
    if (isInline) {
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog || !isOpen) {
      return;
    }
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        // Ignore IME composition-cancel, and defer to any popover/menu layered
        // on top of this dialog so a single Escape closes only the top-most
        // layer (the popover's own focus trap handles it and stops propagation).
        if (isImeKeyEvent(event) || hasActiveFocusTrapEscape()) {
          return;
        }
        event.preventDefault();
        if (allowEscape) {
          onOpenChange(false);
        }
      }
    };
    dialog.addEventListener('keydown', handleKeyDown);
    return () => dialog.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isInline, allowEscape, onOpenChange]);

  // Dev-time guardrail: an open modal should always have an accessible name.
  // The header-title check reads the DOM, so this stays in an effect; the ref
  // keeps it to one warning per component instance.
  const warnedUnnamedDialogRef = useRef(false);
  useEffect(() => {
    const hasHeaderTitle = dialogRef.current?.querySelector(`#${CSS.escape(titleId)}`) != null;
    if (isOpen && !isInline && !hasConsumerName && !hasHeaderTitle && !warnedUnnamedDialogRef.current) {
      warnedUnnamedDialogRef.current = true;
      devWarn('Dialog', 'open dialog has no accessible name. Add a DialogHeader ' + 'with a `title`, or pass `aria-label`/`aria-labelledby`.');
    }
  }, [isOpen, isInline, hasConsumerName, titleId]);

  // Handle backdrop click — when the user clicks the ::backdrop pseudo-element,
  // the event target is the <dialog> element itself; clicks on child content
  // always target the child. This avoids false positives from native browser
  // popups (date pickers, selects, color pickers) that render outside the
  // dialog's bounding rect.
  const handleClick = event => {
    if (event.target === event.currentTarget && allowBackdropClick) {
      onOpenChange(false);
    }
  };

  // Handle native cancel event (browser Escape handling)
  const handleCancel = event => {
    event.preventDefault();
    // Defer to a popover/menu layered on top of this dialog; it will dismiss
    // itself on the same Escape press.
    if (hasActiveFocusTrapEscape()) {
      return;
    }
    if (allowEscape) {
      onOpenChange(false);
    }
  };

  // Shared inner content wrapper
  const innerContent = /*#__PURE__*/_jsx("div", {
    ...stylex.props(styles.inner, ...container(useThemeDefault ? {
      useThemeDefault: 'dialog',
      maxHeight: isFullscreen ? undefined : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
    } : {
      paddingInnerX: paddingToken,
      paddingInnerY: paddingToken,
      paddingOuterX: paddingToken,
      paddingOuterY: paddingToken,
      maxHeight: isFullscreen ? undefined : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
    }), !useThemeDefault && effectivePadding !== 4 && paddingStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingInlineVarStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingBlockStartVarStyles[effectivePadding], !useThemeDefault && effectivePadding !== 4 && containerPaddingBlockEndVarStyles[effectivePadding]),
    children: /*#__PURE__*/_jsx(DialogContext, {
      value: dialogContextValue,
      children: children
    })
  });

  // Filter out native open to prevent InvalidStateError when accidentally passed
  const hasPosition = position != null && !isFullscreen;
  const {
    open: _open,
    ...safeProps
  } = props;

  // --- Inline rendering path (for documentation previews) ---
  if (isInline) {
    if (!isOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      ...safeProps,
      ...mergeProps(themeProps('dialog', {
        variant
      }), stylex.props(styles.inlineWrapper, !isFullscreen && dynamicStyles.sizing(width, maxHeight), isFullscreen && styles.fullscreen, xstyle), className, style),
      "data-testid": props['data-testid'],
      children: innerContent
    });
  }

  // --- Standard modal rendering path ---

  return /*#__PURE__*/_jsx("dialog", {
    ref: mergeRefs(ref, attachDialog),
    ...safeProps,
    ...mergeProps(themeProps('dialog', {
      variant
    }), stylex.props(styles.dialog, isOpen && styles.open, styles.backdrop, !isFullscreen && dynamicStyles.sizing(width, maxHeight), hasPosition && (() => {
      const o = resolveDialogPositionOffsets(position);
      return dynamicStyles.position(o.top, o.insetInlineStart, o.insetInlineEnd, o.bottom);
    })(), isFullscreen && styles.fullscreen, xstyle), className, style),
    onClick: handleClick,
    onCancel: handleCancel,
    "aria-modal": "true"
    // The default aria-labelledby (from a DialogHeader title) is set
    // imperatively in `attachDialog`; a consumer-provided aria-labelledby
    // flows through {...safeProps} above and wins.
    ,
    ...(purpose === 'required' ? {
      role: 'alertdialog'
    } : undefined),
    children: innerContent
  });
}
Dialog.displayName = 'Dialog';